import { _PdfStream } from './base-stream';
import { _PdfDictionary, _PdfReferenceSet, _isCommand, _PdfReference, _PdfName } from './pdf-primitives';
import { BaseException, FormatError, _escapePdfName, _bytesToString, ParserEndOfFileException, _numberToString, _stringToPdfString, _stringToBigEndianBytes, _getSize, _compressStream, _hasUnicodeCharacters, _stringToBytes, _byteArrayToHexString } from './utils';
import { _PdfParser, _PdfLexicalOperator } from './pdf-parser';
import { _PdfBaseStream } from './base-stream';
import { PdfCrossReferenceType } from './enumerator';
import { _MD5, _PdfEncryptor } from './security/encryptor';
export class _PdfCrossReference {
    constructor(document, password) {
        this._version = '';
        this._newLine = '\r\n';
        this._password = password;
        this._document = document;
        this._stream = document._stream;
        this._entries = [];
        this._crossReferencePosition = Object.create(null);
        this._cacheMap = new Map(); // eslint-disable-line
        this._offsetReference = new Map(); // eslint-disable-line
        this._pendingRefs = new _PdfReferenceSet();
        this._offsets = [];
    }
    _setStartXRef(startXRef) {
        this._startXRefQueue = [startXRef];
        this._prevStartXref = startXRef;
        if (typeof this._prevXRefOffset === 'undefined' || this._prevXRefOffset === null) {
            this._prevXRefOffset = startXRef;
        }
    }
    _parse(recoveryMode) {
        let trailerDictionary;
        if (!recoveryMode) {
            trailerDictionary = this._readXRef();
        }
        else {
            trailerDictionary = this._indexObjects();
        }
        trailerDictionary.assignXref(this);
        const entrySize = trailerDictionary.get('Size');
        if (this._entries.length < entrySize || this._entries.length === entrySize) {
            this._nextReferenceNumber = entrySize;
        }
        else if (this._entries.length > entrySize) {
            this._nextReferenceNumber = this._entries.length > 0 ? this._entries.length : 1;
        }
        this._trailer = trailerDictionary;
        const encrypt = trailerDictionary.get('Encrypt');
        if (encrypt) {
            this._document._isEncrypted = true;
            this._ids = trailerDictionary.get('ID');
            this._permissionFlags = encrypt.get('P');
            const fileId = this._ids && this._ids.length ? this._ids[0] : '';
            encrypt.suppressEncryption = true;
            this._encrypt = new _PdfEncryptor(encrypt, fileId, this._password);
            this._document._isUserPassword = this._encrypt._isUserPassword;
            this._document._encryptOnlyAttachment = this._encrypt._encryptOnlyAttachment;
            if (this._document.fileStructure.isIncrementalUpdate) {
                this._document.fileStructure.crossReferenceType = PdfCrossReferenceType.stream;
            }
            else {
                this._document.fileStructure.crossReferenceType = PdfCrossReferenceType.table;
            }
            if (this._encrypt._encryptOnlyAttachment) {
                this._document._hasUserPasswordOnly = true;
                this._document._encryptMetaData = false;
            }
            else {
                this._document._hasUserPasswordOnly = this._encrypt._hasUserPasswordOnly;
                this._document._encryptMetaData = encrypt.has('EncryptMetadata') ? encrypt.get('EncryptMetadata') : true;
            }
        }
        let hasRoot = false;
        let root;
        try {
            root = trailerDictionary.get('Root');
        }
        catch (e) {
            throw new BaseException('Invalid cross reference', 'XRefParseException');
        }
        if (root) {
            try {
                const pagesEntry = root.get('Pages');
                if (pagesEntry) {
                    this._root = root;
                    hasRoot = true;
                }
            }
            catch (ex) {
                throw new BaseException('Invalid cross reference', 'InvalidXRef');
            }
        }
        if (!hasRoot) {
            if (!recoveryMode) {
                throw new BaseException('Invalid cross reference', 'XRefParseException');
            }
            else {
                throw new BaseException('Invalid cross reference', 'InvalidXRef');
            }
        }
    }
    _getEntry(i) {
        const xrefEntry = this._entries[i]; // eslint-disable-line
        if (xrefEntry && !xrefEntry.free && xrefEntry.offset) {
            return xrefEntry;
        }
        return null;
    }
    _fetch(ref, suppressEncryption) {
        let entry; // eslint-disable-line
        if (!(ref instanceof _PdfReference)) {
            throw new Error('ref object is not a reference');
        }
        const objectNumber = ref.objectNumber;
        const cacheEntry = this._cacheMap.get(ref); // eslint-disable-line
        if (typeof cacheEntry !== 'undefined') {
            if (cacheEntry instanceof _PdfDictionary && !cacheEntry.objId) {
                cacheEntry.objId = objectNumber;
            }
            return cacheEntry;
        }
        const xrefEntry = this._getEntry(objectNumber);
        if (xrefEntry === null) {
            this._cacheMap.set(ref, xrefEntry);
            return xrefEntry;
        }
        if (this._pendingRefs.has(ref)) {
            this._pendingRefs.remove(ref);
            throw new Error('circular reference');
        }
        this._pendingRefs.put(ref);
        try {
            if (xrefEntry.uncompressed) {
                entry = this._fetchUncompressed(ref, xrefEntry, suppressEncryption);
            }
            else {
                entry = this._fetchCompressed(ref, xrefEntry);
            }
            this._pendingRefs.remove(ref);
        }
        catch (ex) {
            this._pendingRefs.remove(ref);
            throw ex;
        }
        return entry;
    }
    _fetchUncompressed(reference, xrefEntry, makeFilter) {
        const generationNumber = reference.generationNumber;
        const objectNumber = reference.objectNumber;
        if (xrefEntry.gen !== generationNumber) {
            throw new BaseException(`Inconsistent generation in XRef: ${reference}`, 'XRefEntryException');
        }
        const stream = this._stream.makeSubStream(xrefEntry.offset + this._stream.start, undefined);
        const parser = new _PdfParser(new _PdfLexicalOperator(stream), this, true, false, this._encrypt);
        const obj1 = parser.getObject();
        const obj2 = parser.getObject();
        const obj3 = parser.getObject();
        if (obj1 !== objectNumber || obj2 !== generationNumber || typeof obj3 === 'undefined') {
            throw new BaseException(`Bad (uncompressed) XRef entry: ${reference}`, 'XRefEntryException');
        }
        let entry; // eslint-disable-line
        if (this._encrypt && !makeFilter) {
            entry = parser.getObject(reference.objectNumber, reference.generationNumber, true);
        }
        else {
            entry = parser.getObject(null, makeFilter);
        }
        if (!(entry instanceof _PdfBaseStream)) {
            this._cacheMap.set(reference, entry);
        }
        if (entry instanceof _PdfDictionary) {
            entry.objId = reference.toString();
        }
        else if (entry instanceof _PdfBaseStream) {
            entry.dictionary.objId = reference.toString();
        }
        return entry;
    }
    _fetchCompressed(ref, xrefEntry) {
        const tableOffset = xrefEntry.offset;
        const stream = this._fetch(_PdfReference.get(tableOffset, 0));
        if (typeof stream === 'undefined') {
            throw new FormatError('bad ObjStm stream');
        }
        const first = stream.dictionary.get('First');
        const n = stream.dictionary.get('N');
        const gen = ref.generationNumber;
        if (!Number.isInteger(first) || !Number.isInteger(n)) {
            throw new FormatError('invalid first and n parameters for ObjStm stream');
        }
        let parser = new _PdfParser(new _PdfLexicalOperator(stream), this, true);
        const nums = new Array(n);
        const offsets = new Array(n);
        for (let i = 0; i < n; ++i) {
            const value = parser.getObject();
            if (!Number.isInteger(value)) {
                throw new FormatError(`invalid object number in the ObjStm stream: ${value}`);
            }
            const offset = parser.getObject();
            if (!Number.isInteger(offset)) {
                throw new FormatError(`invalid object offset in the ObjStm stream: ${offset}`);
            }
            nums[i] = value; // eslint-disable-line
            offsets[i] = offset; // eslint-disable-line
        }
        const start = (stream.start || 0) + first;
        const entries = new Array(n); // eslint-disable-line
        for (let i = 0; i < n; ++i) {
            const length = (i < n - 1 ? (offsets[i + 1] - offsets[i]) : undefined); // eslint-disable-line
            if (length < 0) {
                throw new FormatError('Invalid offset in the ObjStm stream.');
            }
            parser = new _PdfParser(new _PdfLexicalOperator(stream.makeSubStream(start + offsets[i], length, stream.dictionary)), this, true); // eslint-disable-line
            const obj = parser.getObject(); // eslint-disable-line
            entries[i] = obj; // eslint-disable-line
            if (obj instanceof _PdfBaseStream) {
                continue;
            }
            const value = nums[i]; // eslint-disable-line
            const entry = this._entries[value]; // eslint-disable-line
            if (entry && entry.offset === tableOffset && entry.gen === i) {
                const objId = `${value} ${gen}`;
                this._cacheMap.set(_PdfReference.get(value, gen), obj);
                if (obj instanceof _PdfDictionary) {
                    obj.objId = objId;
                }
            }
        }
        const result = entries[xrefEntry.gen]; // eslint-disable-line
        if (typeof result === 'undefined') {
            throw new BaseException(`Bad (compressed) XRef entry: ${ref}`, 'XRefEntryException');
        }
        return result;
    }
    _readXRef(recoveryMode = false) {
        const stream = this._stream;
        const startXRefParsedCache = new Set();
        try {
            while (this._startXRefQueue.length) {
                const startXRef = this._startXRefQueue[0];
                if (this._prevStartXref < startXRef) {
                    this._prevStartXref = startXRef;
                }
                if (startXRefParsedCache.has(startXRef)) {
                    this._startXRefQueue.shift();
                    continue;
                }
                startXRefParsedCache.add(startXRef);
                stream.position = startXRef + stream.start;
                const parser = new _PdfParser(new _PdfLexicalOperator(stream), this, true);
                let obj = parser.getObject(); // eslint-disable-line
                let dictionary;
                if (_isCommand(obj, 'xref')) {
                    if (typeof this._document._fileStructure._crossReferenceType === 'undefined') {
                        this._document._fileStructure._crossReferenceType = PdfCrossReferenceType.table;
                    }
                    dictionary = this._processXRefTable(parser);
                    if (!this._topDictionary) {
                        this._topDictionary = dictionary;
                    }
                    obj = dictionary.get('XRefStm');
                    if (Number.isInteger(obj)) {
                        const position = obj; // eslint-disable-line
                        if (!(position in this._crossReferencePosition)) {
                            this._crossReferencePosition[position] = 1; // eslint-disable-line
                            this._startXRefQueue.push(position);
                        }
                    }
                }
                else if (Number.isInteger(obj)) {
                    if (typeof this._document._fileStructure._crossReferenceType === 'undefined') {
                        this._document._fileStructure._crossReferenceType = PdfCrossReferenceType.stream;
                    }
                    const gen = parser.getObject();
                    const command = parser.getObject();
                    obj = parser.getObject();
                    if (typeof gen === 'undefined' ||
                        !Number.isInteger(gen) ||
                        !_isCommand(command, 'obj') ||
                        !(obj instanceof _PdfBaseStream)) {
                        throw new FormatError('Invalid cross reference stream');
                    }
                    dictionary = this._processXRefStream(obj);
                    if (!this._topDictionary) {
                        this._topDictionary = dictionary;
                    }
                    if (!dictionary) {
                        throw new FormatError('Failed to read XRef stream');
                    }
                }
                else {
                    throw new FormatError('Invalid XRef stream header');
                }
                obj = dictionary.get('Prev');
                if (Number.isInteger(obj)) {
                    this._startXRefQueue.push(obj);
                }
                else if (obj instanceof _PdfReference) {
                    this._startXRefQueue.push(obj.objectNumber);
                }
                this._startXRefQueue.shift();
            }
            return this._topDictionary;
        }
        catch (e) {
            this._startXRefQueue.shift();
        }
        if (recoveryMode) {
            return undefined;
        }
        throw new BaseException('Invalid cross reference', 'XRefParseException');
    }
    _readToken(data, offset) {
        const lf = 0xa;
        const cr = 0xd;
        const lt = 0x3c;
        let token = '';
        let ch = data[offset]; // eslint-disable-line
        while (ch !== lf && ch !== cr && ch !== lt) {
            if (++offset >= data.length) {
                break;
            }
            token += String.fromCharCode(ch);
            ch = data[offset]; // eslint-disable-line
        }
        return token;
    }
    _skipUntil(data, offset, what) {
        const length = what.length;
        const dataLength = data.length;
        let skipped = 0;
        while (offset < dataLength) {
            let i = 0;
            while (i < length && data[offset + i] === what[i]) { // eslint-disable-line
                ++i;
            }
            if (i >= length) {
                break;
            }
            offset++;
            skipped++;
        }
        return skipped;
    }
    _indexObjects() {
        const tab = 0x9;
        const lf = 0xa;
        const cr = 0xd;
        const space = 0x20;
        const percent = 0x25;
        const objRegExp = /^(\d+)\s+(\d+)\s+obj\b/;
        const endobjRegExp = /\bendobj[\b\s]$/;
        const nestedObjRegExp = /\s+(\d+\s+\d+\s+obj[\b\s<])$/;
        const checkContentLength = 25;
        const trailerBytes = new Uint8Array([116, 114, 97, 105, 108, 101, 114]);
        const startxrefBytes = new Uint8Array([115, 116, 97, 114, 116, 120, 114, 101, 102]);
        const objBytes = new Uint8Array([111, 98, 106]);
        const xrefBytes = new Uint8Array([47, 88, 82, 101, 102]);
        this._entries.length = 0;
        this._cacheMap.clear();
        const stream = this._stream;
        stream.position = 0;
        const buffer = stream.getBytes();
        const length = buffer.length;
        let position = stream.start;
        const trailers = [];
        const crossReferencePosition = [];
        while (position < length) {
            let ch = buffer[position]; // eslint-disable-line
            if (ch === tab || ch === lf || ch === cr || ch === space) {
                ++position;
                continue;
            }
            if (ch === percent) {
                do {
                    ++position;
                    if (position >= length) {
                        break;
                    }
                    ch = buffer[position]; // eslint-disable-line
                } while (ch !== lf && ch !== cr);
                continue;
            }
            const token = this._readToken(buffer, position);
            let m; // eslint-disable-line
            if (token.startsWith('xref') && (token.length === 4 || /\s/.test(token[4]))) {
                position += this._skipUntil(buffer, position, trailerBytes);
                trailers.push(position);
                position += this._skipUntil(buffer, position, startxrefBytes);
            }
            else {
                m = objRegExp.exec(token);
                if (m) {
                    const objectNumber = (m[1]) | 0;
                    const gen = (m[2]) | 0;
                    let contentLength;
                    let startPos = position + token.length;
                    let updateEntries = false;
                    if (!this._entries[objectNumber]) { // eslint-disable-line
                        updateEntries = true;
                    }
                    else if (this._entries[objectNumber].gen === gen) { // eslint-disable-line
                        try {
                            const subStream = stream.makeSubStream(startPos, stream.length - startPos);
                            const lexicalOperator = new _PdfLexicalOperator(subStream);
                            const parser = new _PdfParser(lexicalOperator, null);
                            parser.getObject();
                            updateEntries = true;
                        }
                        catch (ex) {
                            updateEntries = !(ex instanceof ParserEndOfFileException);
                        }
                    }
                    if (updateEntries) {
                        const info = new _PdfObjectInformation();
                        info.offset = position - stream.start;
                        info.gen = gen;
                        info.uncompressed = true;
                        this._entries[objectNumber] = info; // eslint-disable-line
                    }
                    while (startPos < buffer.length) {
                        const endPos = startPos + this._skipUntil(buffer, startPos, objBytes) + 4;
                        contentLength = endPos - position;
                        const checkPos = Math.max(endPos - checkContentLength, startPos);
                        const tokenStr = _bytesToString(buffer.subarray(checkPos, endPos));
                        if (endobjRegExp.test(tokenStr)) {
                            break;
                        }
                        else {
                            const objToken = nestedObjRegExp.exec(tokenStr); // eslint-disable-line
                            if (objToken && objToken[1]) {
                                contentLength -= objToken[1].length;
                                break;
                            }
                        }
                        startPos = endPos;
                    }
                    const content = buffer.subarray(position, position + contentLength);
                    const xrefTagOffset = this._skipUntil(content, 0, xrefBytes);
                    if (xrefTagOffset < contentLength && content[xrefTagOffset + 5] < 64) {
                        crossReferencePosition.push(position - stream.start);
                        this._crossReferencePosition[position - stream.start] = 1;
                    }
                    position += contentLength;
                }
                else if (token.startsWith('trailer') && (token.length === 7 || /\s/.test(token[7]))) {
                    trailers.push(position);
                    position += this._skipUntil(buffer, position, startxrefBytes);
                }
                else {
                    position += token.length + 1;
                }
            }
        }
        for (let i = 0; i < crossReferencePosition.length; ++i) {
            this._startXRefQueue.push(crossReferencePosition[i]); // eslint-disable-line
            this._readXRef(true);
        }
        let trailerDict;
        for (let i = 0; i < trailers.length; ++i) {
            stream.position = trailers[i]; // eslint-disable-line
            const parser = new _PdfParser(new _PdfLexicalOperator(stream), this, true, true);
            const obj = parser.getObject(); // eslint-disable-line
            if (!_isCommand(obj, 'trailer')) {
                continue;
            }
            const dictionary = parser.getObject(); // eslint-disable-line
            if (!(dictionary instanceof _PdfDictionary)) {
                continue;
            }
            try {
                const rootDict = dictionary.get('Root'); // eslint-disable-line
                if (!(rootDict instanceof _PdfDictionary)) {
                    continue;
                }
                const pagesDict = rootDict.get('Pages'); // eslint-disable-line
                if (!(pagesDict instanceof _PdfDictionary)) {
                    continue;
                }
                const pagesCount = pagesDict.get('Count');
                if (typeof pagesCount === 'undefined' || !Number.isInteger(pagesCount)) {
                    continue;
                }
            }
            catch (ex) {
                continue;
            }
            if (dictionary.has('ID')) {
                return dictionary;
            }
            trailerDict = dictionary;
        }
        if (trailerDict) {
            return trailerDict;
        }
        if (this._topDictionary) {
            return this._topDictionary;
        }
        throw new BaseException('Invalid PDF structure.', 'InvalidPDFException');
    }
    _processXRefTable(parser) {
        if (typeof this._tableState === 'undefined') {
            const tableState = new _PdfCrossTableState();
            tableState.entryNum = 0;
            tableState.streamPos = parser.lexicalOperator.stream.position;
            tableState.parserBuf1 = parser.first;
            tableState.parserBuf2 = parser.second;
            this._tableState = tableState;
        }
        const obj = this._readXRefTable(parser);
        if (!_isCommand(obj, 'trailer')) {
            throw new FormatError('Invalid XRef table: could not find trailer dictionary');
        }
        let topDictionary = parser.getObject(); // eslint-disable-line
        let dictionary;
        if (topDictionary) {
            if (topDictionary instanceof _PdfDictionary) {
                dictionary = topDictionary;
            }
            else if (topDictionary instanceof _PdfBaseStream && topDictionary.dictionary) {
                dictionary = topDictionary.dictionary;
            }
        }
        if (!dictionary) {
            throw new FormatError('Invalid cross reference: could not parse trailer dictionary');
        }
        this._tableState = undefined;
        return dictionary;
    }
    _readXRefTable(parser) {
        const stream = parser.lexicalOperator.stream;
        stream.position = this._tableState.streamPos;
        parser.first = this._tableState.parserBuf1;
        parser.second = this._tableState.parserBuf2;
        let obj; // eslint-disable-line
        while (true) { // eslint-disable-line
            if (typeof this._tableState.firstEntryNum === 'undefined' || typeof this._tableState.entryCount === 'undefined') {
                obj = parser.getObject();
                if (_isCommand(obj, 'trailer')) {
                    break;
                }
                this._tableState.firstEntryNum = obj;
                this._tableState.entryCount = parser.getObject();
            }
            let first = this._tableState.firstEntryNum;
            const count = this._tableState.entryCount;
            if (!Number.isInteger(first) || !Number.isInteger(count)) {
                throw new FormatError('Invalid cross reference: wrong types in subsection header');
            }
            for (let i = this._tableState.entryNum; i < count; i++) {
                this._tableState.streamPos = stream.position;
                this._tableState.entryNum = i;
                this._tableState.parserBuf1 = parser.first;
                this._tableState.parserBuf2 = parser.second;
                const entry = new _PdfObjectInformation();
                entry.offset = parser.getObject();
                entry.gen = parser.getObject();
                const type = parser.getObject();
                if (type) {
                    switch (type.command) {
                        case 'f':
                            entry.free = true;
                            break;
                        case 'n':
                            entry.uncompressed = true;
                            break;
                    }
                }
                if (!Number.isInteger(entry.offset) || !Number.isInteger(entry.gen) || !(entry.free || entry.uncompressed)) {
                    throw new FormatError(`Invalid entry in cross reference subsection: ${first}, ${count}`);
                }
                if (i === 0 && entry.free && first === 1) {
                    first = 0;
                }
                if (!this._entries[i + first]) {
                    this._entries[i + first] = entry;
                }
            }
            this._tableState.entryNum = 0;
            this._tableState.streamPos = stream.position;
            this._tableState.parserBuf1 = parser.first;
            this._tableState.parserBuf2 = parser.second;
            this._tableState.firstEntryNum = undefined;
            this._tableState.entryCount = undefined;
        }
        if (this._entries[0] && !this._entries[0].free) {
            throw new FormatError('Invalid XRef table: unexpected first object');
        }
        return obj;
    }
    _processXRefStream(stream) {
        if (typeof this._streamState === 'undefined') {
            const streamParameters = stream.dictionary;
            const streamState = new _PdfStreamState();
            let index = streamParameters.getArray('Index');
            if (!index) {
                index = [0, streamParameters.get('Size')];
            }
            streamState.entryRanges = index;
            streamState.byteWidths = streamParameters.getArray('W');
            streamState.entryNum = 0;
            streamState.streamPos = stream.position;
            this._streamState = streamState;
        }
        this._readXRefStream(stream);
        this._streamState = undefined;
        return stream.dictionary;
    }
    _readXRefStream(stream) {
        stream.position = this._streamState.streamPos;
        const typeFieldWidth = this._streamState.byteWidths[0];
        const offsetFieldWidth = this._streamState.byteWidths[1];
        const generationFieldWidth = this._streamState.byteWidths[2];
        const entryRanges = this._streamState.entryRanges;
        while (entryRanges.length > 0) {
            const first = entryRanges[0];
            const n = entryRanges[1];
            if (!Number.isInteger(first) || !Number.isInteger(n)) {
                throw new FormatError(`Invalid XRef range fields: ${first}, ${n}`);
            }
            if (!Number.isInteger(typeFieldWidth) || !Number.isInteger(offsetFieldWidth) || !Number.isInteger(generationFieldWidth)) {
                throw new FormatError(`Invalid XRef entry fields length: ${first}, ${n}`);
            }
            for (let i = this._streamState.entryNum; i < n; ++i) {
                this._streamState.entryNum = i;
                this._streamState.streamPos = stream.position;
                let type = 0;
                let offset = 0;
                let generation = 0;
                for (let j = 0; j < typeFieldWidth; ++j) {
                    const typeByte = stream.getByte();
                    if (typeByte === -1) {
                        throw new FormatError('invalid cross reference byte width type.');
                    }
                    type = (type << 8) | typeByte;
                }
                if (typeFieldWidth === 0) {
                    type = 1;
                }
                for (let j = 0; j < offsetFieldWidth; ++j) {
                    const offsetByte = stream.getByte();
                    if (offsetByte === -1) {
                        throw new FormatError('invalid cross reference byte width offset.');
                    }
                    offset = (offset << 8) | offsetByte;
                }
                for (let j = 0; j < generationFieldWidth; ++j) {
                    const generationByte = stream.getByte();
                    if (generationByte === -1) {
                        throw new FormatError('invalid cross reference byte width generation.');
                    }
                    generation = (generation << 8) | generationByte;
                }
                const entry = new _PdfObjectInformation();
                entry.offset = offset;
                entry.gen = generation;
                switch (type) {
                    case 0:
                        entry.free = true;
                        break;
                    case 1:
                        entry.uncompressed = true;
                        break;
                    case 2:
                        break;
                    default:
                        throw new FormatError(`Invalid XRef entry type: ${type}`);
                }
                if (!this._entries[first + i]) {
                    this._entries[first + i] = entry;
                }
            }
            this._streamState.entryNum = 0;
            this._streamState.streamPos = stream.position;
            entryRanges.splice(0, 2);
        }
    }
    _getCatalogObj() {
        return this._root;
    }
    _save() {
        const buffer = [37, 80, 68, 70, 45];
        this._writeString(`${this._version}${this._newLine}`, buffer);
        buffer.push(0x25, 0x83, 0x92, 0xfa, 0xfe);
        this._writeString(this._newLine, buffer);
        if (!this._document.fileStructure.isIncrementalUpdate) {
            this._currentLength = 0;
            const objectCollection = new _PdfMainObjectCollection(this);
            this._writeObjectCollection(objectCollection._mainObjectCollection, buffer);
            const stream = new _PdfStream(buffer);
            this._stream = stream;
            this._document._stream = stream;
            const array = new Uint8Array(this._stream.length);
            array.set(this._stream.bytes);
            array.set(buffer, 0);
            return array;
        }
        else {
            this._currentLength = this._stream.length;
            const buffer = [37, 80, 68, 70, 45];
            this._writeString(`${this._version}${this._newLine}`, buffer);
            buffer.push(0x25, 0x83, 0x92, 0xfa, 0xfe);
            this._writeString(this._newLine, buffer);
            if (this._document._fileStructure._crossReferenceType === PdfCrossReferenceType.stream) {
                this._saveAsStream(this._currentLength, buffer);
            }
            else {
                this._saveAsTable(this._currentLength, buffer);
            }
            const array = new Uint8Array(this._stream.length + buffer.length);
            array.set(this._stream.bytes);
            array.set(buffer, this._stream.length);
            return array;
        }
    }
    _saveAsStream(currentLength, buffer) {
        const objectStreamCollection = new Map();
        this._indexes = [];
        this._indexes.push(0, 1);
        this._offsets = [];
        this._cacheMap.forEach((value, key) => {
            if (value instanceof _PdfBaseStream) {
                const dictionary = value.dictionary;
                if (dictionary && dictionary._updated && (!dictionary.isCatalog || this._allowCatalog) && !dictionary._isProcessed) {
                    let cipher;
                    if (this._encrypt) {
                        cipher = this._encrypt._createCipherTransform(key.objectNumber, key.generationNumber);
                    }
                    this._updatedDictionary(currentLength, key, buffer, value, cipher);
                    dictionary._isProcessed = true;
                }
            }
        });
        this._cacheMap.forEach((value, key) => {
            if (value instanceof _PdfDictionary) {
                if (value._updated && !value.isCatalog && !value._isProcessed) {
                    this._writeArchiveStream(objectStreamCollection, key, value);
                }
                else if (value._updated && (value.isCatalog || this._allowCatalog)) {
                    this._updatedDictionary(currentLength, key, buffer, value);
                }
            }
            else if (value instanceof _PdfBaseStream) {
                const dictionary = value.dictionary;
                if (dictionary && dictionary._updated && (!dictionary.isCatalog || this._allowCatalog) && !dictionary._isProcessed) {
                    this._updatedDictionary(currentLength, key, buffer, value);
                }
            }
        });
        this._objectStream = undefined;
        this._objectStreamCollection = objectStreamCollection;
        this._writeXrefStream(buffer);
    }
    _updatedDictionary(currentLength, key, buffer, value, // eslint-disable-line
    cipher) {
        this._indexes.push(key.objectNumber, 1);
        this._offsets.push(currentLength + buffer.length);
        this._writeObject(value, buffer, key, cipher);
        value._updated = false;
    }
    _writeXrefStream(buffer) {
        this._objectStreamCollection.forEach((value, key) => {
            value._save(buffer, this._currentLength);
            if (Array.isArray(value._collection)) {
                this._indexes.push(...value._collection);
            }
            this._indexes.push(key.objectNumber, 1);
        });
        const formatValue = Math.max(_getSize(this._currentLength + buffer.length), _getSize(this._nextReferenceNumber));
        const newRef = this._getNextReference();
        this._indexes.push(newRef.objectNumber, 1);
        const newStartXref = this._currentLength + buffer.length;
        const newXref = new _PdfDictionary(this);
        newXref.set('Type', _PdfName.get('XRef'));
        newXref.set('Index', this._indexes);
        newXref.set('W', [1, formatValue, 1]);
        this._copyTrailer(newXref);
        if (this._ids && this._ids.length > 0) {
            newXref.update('ID', [this._ids[0], this._computeMessageDigest(newStartXref)]);
        }
        const newXrefData = [];
        this._writeLong(0, 1, newXrefData);
        this._writeLong(0, formatValue, newXrefData);
        this._writeLong(-1, 1, newXrefData);
        if (this._offsets.length > 0) {
            for (let index = 0; index < this._offsets.length; index++) {
                this._writeLong(1, 1, newXrefData);
                this._writeLong(this._offsets[index], formatValue, newXrefData); // eslint-disable-line
                this._writeLong(0, 1, newXrefData);
            }
        }
        if (this._objectStreamCollection.size > 0) {
            this._objectStreamCollection.forEach((value, key) => {
                for (let index = 0; index < value._length; index++) {
                    this._writeLong(2, 1, newXrefData);
                    this._writeLong(key.objectNumber, formatValue, newXrefData);
                    this._writeLong(index, 1, newXrefData);
                }
                this._writeLong(1, 1, newXrefData);
                this._writeLong(value._archiveOffset, formatValue, newXrefData);
                this._writeLong(0, 1, newXrefData);
            });
        }
        this._writeLong(1, 1, newXrefData);
        this._writeLong(newStartXref, formatValue, newXrefData);
        this._writeLong(0, 1, newXrefData);
        newXref.set('Length', newXrefData.length);
        const newXrefStream = new _PdfStream(newXrefData, newXref, 0, newXrefData.length);
        let cipher;
        if (this._encrypt) {
            cipher = this._encrypt._createCipherTransform(newRef.objectNumber, newRef.generationNumber);
        }
        this._writeObject(newXrefStream, buffer, newRef, cipher, true);
        this._writeString(`startxref${this._newLine}${newStartXref}${this._newLine}%%EOF${this._newLine}`, buffer);
    }
    _saveAsTable(currentLength, buffer) {
        let tempBuffer = '';
        this._cacheMap.forEach((value, key) => {
            let dictionary;
            if (value instanceof _PdfDictionary) {
                dictionary = value;
            }
            else if (value instanceof _PdfBaseStream) {
                dictionary = value.dictionary;
            }
            if (dictionary && dictionary._updated && (!dictionary.isCatalog || this._allowCatalog)) {
                const offsetString = this._processString((currentLength + buffer.length).toString(), 10);
                const genString = this._processString(key.generationNumber.toString(), 5);
                tempBuffer += `${key.objectNumber} 1${this._newLine}${offsetString} ${genString} n${this._newLine}`;
                this._writeObject(value, buffer, key);
            }
        });
        const newStartXref = buffer.length + currentLength;
        this._writeString(`xref${this._newLine}0 1${this._newLine}0000000000 65535 f${this._newLine}`, buffer);
        this._writeXref(buffer, tempBuffer, newStartXref);
    }
    _writeXref(buffer, tempBuffer, newStartXref) {
        this._writeString(tempBuffer, buffer);
        this._writeString(`trailer${this._newLine}`, buffer);
        const newXref = new _PdfDictionary(this);
        this._copyTrailer(newXref);
        this._writeDictionary(newXref, buffer, this._newLine);
        this._writeString(`startxref${this._newLine}${newStartXref}${this._newLine}%%EOF${this._newLine}`, buffer);
    }
    _writeXrefTable(buffer) {
        let tempBuffer = '';
        let collection = this._getSortedReferences(this._offsetReference); // eslint-disable-line
        collection.forEach((value, key) => {
            const offsetString = this._processString(value.toString(), 10);
            const genString = this._processString(key.generationNumber ? '0' : '', 5);
            if (value !== 0) {
                tempBuffer += `${offsetString} ${genString} n${this._newLine}`;
            }
            else {
                tempBuffer += `${offsetString} ${genString} f${this._newLine}`;
            }
        });
        const newStartXref = buffer.length;
        const xrefHeader = `xref${this._newLine}`;
        const xrefEntry = `0 ${collection.size + 1}${this._newLine}`;
        const initialEntry = `0000000000 65535 f${this._newLine}`;
        this._writeString(xrefHeader + xrefEntry + initialEntry, buffer);
        this._writeXref(buffer, tempBuffer, newStartXref);
    }
    _processString(value, length) {
        while (value.length < length) {
            value = '0' + value;
        }
        return value;
    }
    _copyTrailer(newXref) {
        const reference = this._getNextReference();
        newXref.set('Size', reference.objectNumber);
        if (this._document.fileStructure.isIncrementalUpdate) {
            newXref.set('Prev', this._prevXRefOffset);
        }
        const root = this._trailer.getRaw('Root'); // eslint-disable-line
        if (typeof root !== 'undefined' && root !== null) {
            newXref.set('Root', root);
        }
        const info = this._trailer.getRaw('Info'); // eslint-disable-line
        if (typeof info !== 'undefined' && info !== null) {
            newXref.set('Info', info);
        }
        const encrypt = this._trailer.getRaw('Encrypt'); // eslint-disable-line
        if (typeof encrypt !== 'undefined' && encrypt !== null) {
            newXref.set('Encrypt', encrypt);
        }
    }
    _computeMessageDigest(size) {
        const time = Math.floor(Date.now() / 1000);
        const buffer = [time.toString(), '', size.toString()];
        const info = this._trailer.getRaw('Info');
        const crossReferenceInfo = new _PdfDictionary();
        if (info && info instanceof _PdfDictionary) {
            info.forEach((key, value) => {
                if (value && typeof value === 'string') {
                    crossReferenceInfo.set(key, _stringToPdfString(value));
                }
            });
        }
        crossReferenceInfo.forEach((key, value) => {
            buffer.push(value);
        });
        const array = [];
        buffer.forEach((str) => {
            this._writeString(str, array);
        });
        return _bytesToString((new _MD5().hash(new Uint8Array(array))));
    }
    _getNextReference() {
        const reference = new _PdfReference(this._nextReferenceNumber++, 0);
        reference._isNew = true;
        return reference;
    }
    _writeObject(obj, // eslint-disable-line
    buffer, reference, transform, isCrossReference) {
        if (reference && reference instanceof _PdfReference) {
            this._writeString(`${reference.objectNumber} ${reference.generationNumber} obj${this._newLine}`, buffer);
        }
        if (obj instanceof _PdfDictionary) {
            this._writeDictionary(obj, buffer, this._newLine, transform, isCrossReference);
        }
        else if (obj instanceof _PdfBaseStream) {
            this._writeStream(obj, buffer, transform, isCrossReference);
        }
        else if (Array.isArray(obj) && obj.length > 0) {
            this._writeString('[ ', buffer);
            obj.forEach((value, index) => {
                if (value instanceof _PdfReference) {
                    this._writeString(`${value.objectNumber} ${value.generationNumber} R`, buffer);
                }
                else if (Array.isArray(value)) {
                    this._writeString('[ ', buffer);
                    value.forEach((nestedValue) => {
                        if (nestedValue instanceof _PdfReference) {
                            this._writeString(`${nestedValue.objectNumber} ${nestedValue.generationNumber} R`, buffer);
                        }
                        else if (nestedValue instanceof _PdfName) {
                            this._writeString(`/${_escapePdfName(nestedValue.name)}`, buffer);
                        }
                        else {
                            this._writeString(`${nestedValue} `, buffer);
                        }
                    });
                    this._writeString(']', buffer);
                }
                else if (value instanceof _PdfName) {
                    this._writeString(`/${_escapePdfName(value.name)}`, buffer);
                }
                else if (value instanceof _PdfDictionary) {
                    this._writeDictionary(value, buffer, this._newLine, transform, isCrossReference);
                }
                else if (typeof (value) === 'string') {
                    if (_hasUnicodeCharacters(value)) {
                        const bytes = _stringToBytes(value);
                        const text = _byteArrayToHexString(bytes);
                        this._writeString('<', buffer);
                        this._writeString(`${text}`, buffer);
                        this._writeString('>', buffer);
                    }
                    else {
                        this._writeString(`${value}\n`, buffer);
                    }
                }
                else {
                    this._writeString(`${value}\n`, buffer);
                }
                if (index < obj.length - 1) {
                    this._writeString(' ', buffer);
                }
            });
            this._writeString(']', buffer);
            this._writeString('\n', buffer);
        }
        else if (typeof obj === 'number') {
            this._writeString(`${obj}\n`, buffer);
        }
        if (reference && reference instanceof _PdfReference) {
            this._writeString(`endobj${this._newLine}`, buffer);
        }
    }
    _writeDictionary(dictionary, buffer, spaceChar, transform, isCrossReference) {
        if (dictionary._currentObj) {
            dictionary._currentObj._beginSave();
        }
        if (dictionary._isFont) {
            this._writeFontDictionary(dictionary);
        }
        this._writeString(`<<${spaceChar}`, buffer);
        dictionary.forEach((key, value) => {
            this._writeString(`/${_escapePdfName(key)} `, buffer);
            this._writeValue(value, buffer, transform, isCrossReference);
            this._writeString(spaceChar, buffer);
        });
        this._writeString(`>>${this._newLine}`, buffer);
    }
    _writeFontDictionary(dictionary) {
        if (dictionary.has('DescendantFonts')) {
            const fonts = dictionary.get('DescendantFonts'); // eslint-disable-line
            if (!Array.isArray(fonts)) {
                const reference = this._getNextReference();
                this._cacheMap.set(reference, fonts);
                dictionary.update('DescendantFonts', [reference]);
            }
        }
        if (dictionary.has('ToUnicode')) {
            const fonts = dictionary.get('ToUnicode'); // eslint-disable-line
            if (!(fonts instanceof _PdfReference)) {
                const reference = this._getNextReference();
                this._cacheMap.set(reference, fonts);
                dictionary.update('ToUnicode', reference);
            }
        }
        if (dictionary.has('FontFile2')) {
            const fonts = dictionary.get('FontFile2'); // eslint-disable-line
            const reference = this._getNextReference();
            this._cacheMap.set(reference, fonts);
            dictionary.update('FontFile2', reference);
        }
        if (dictionary.has('FontDescriptor')) {
            const fonts = dictionary.get('FontDescriptor'); // eslint-disable-line
            const reference = this._getNextReference();
            this._cacheMap.set(reference, fonts);
            dictionary.update('FontDescriptor', reference);
        }
    }
    _writeStream(stream, buffer, transform, isCrossReference) {
        let value;
        const streamBuffer = [];
        if (!isCrossReference) {
            if (stream._isCompress && !stream._isImage) {
                value = _compressStream(stream);
            }
            else {
                value = stream.getString();
            }
            if (transform) {
                value = transform.encryptString(value);
            }
        }
        else {
            value = stream.getString();
        }
        this._writeString(value, streamBuffer);
        stream.dictionary.update('Length', streamBuffer.length);
        this._writeDictionary(stream.dictionary, buffer, this._newLine, transform, isCrossReference);
        this._writeString(`stream${this._newLine}`, buffer);
        this._writeBytes(streamBuffer, buffer);
        this._writeString(`${this._newLine}endstream${this._newLine}`, buffer);
    }
    _writeValue(value, buffer, transform, isCrossReference) {
        if (value instanceof _PdfName) {
            if (value.name.indexOf(' ') !== -1) {
                value.name = value.name.replace(/ /g, '#20'); // eslint-disable-line
            }
            this._writeString(`/${value.name}`, buffer);
        }
        else if (value instanceof _PdfReference) {
            this._writeString(`${value.toString()} R`, buffer);
        }
        else if (Array.isArray(value)) {
            this._writeString('[', buffer);
            let first = true;
            for (const val of value) {
                if (!first) {
                    this._writeString(' ', buffer);
                }
                else {
                    first = false;
                }
                this._writeValue(val, buffer, transform, isCrossReference);
            }
            this._writeString(']', buffer);
        }
        else if (typeof value === 'string') {
            if (!isCrossReference && transform) {
                value = transform.encryptString(value);
            }
            let isUnicode = false;
            for (let i = 0; i < value.length; i++) {
                if (value.charCodeAt([i]) > 255) {
                    isUnicode = true;
                    break;
                }
            }
            if (isUnicode) {
                this._writeUnicodeString(value, buffer);
            }
            else {
                this._writeString(`(${this._escapeString(value)})`, buffer);
            }
        }
        else if (typeof value === 'number') {
            this._writeString(_numberToString(value), buffer);
        }
        else if (typeof value === 'boolean') {
            this._writeString(value.toString(), buffer);
        }
        else if (value instanceof _PdfDictionary) {
            this._writeDictionary(value, buffer, this._newLine, transform, isCrossReference);
        }
        else if (value instanceof _PdfBaseStream) {
            this._writeStream(value, buffer, transform, isCrossReference);
        }
        else if (value === null) {
            this._writeString('null', buffer);
        }
    }
    _writeUnicodeString(value, buffer) {
        const byteValues = _stringToBigEndianBytes(value);
        byteValues.unshift(254, 255);
        const data = [];
        byteValues.forEach((byte) => {
            switch (byte) {
                case 40:
                case 41:
                    data.push(92);
                    data.push(byte);
                    break;
                case 13:
                    data.push(92);
                    data.push(114);
                    break;
                case 92:
                    data.push(92);
                    data.push(byte);
                    break;
                default:
                    data.push(byte);
                    break;
            }
        });
        buffer.push('('.charCodeAt(0) & 0xff);
        data.forEach((byte) => {
            buffer.push(byte & 0xff);
        });
        buffer.push(')'.charCodeAt(0) & 0xff);
    }
    _writeString(value, buffer) {
        for (let i = 0; i < value.length; i++) {
            buffer.push(value.charCodeAt(i) & 0xff);
        }
    }
    _writeBytes(data, buffer) {
        for (let i = 0; i < data.length; i++) {
            buffer.push(data[i]); // eslint-disable-line
        }
    }
    _writeLong(value, count, buffer) {
        for (let i = count - 1; i >= 0; --i) {
            buffer.push(value >> (i << 3) & 0xff);
        }
    }
    _escapeString(value) {
        return value.replace(/([()\\\n\r])/g, (substring) => {
            if (substring === '\n') {
                return '\\n';
            }
            else if (substring === '\r') {
                return '\\r';
            }
            return `\\${substring}`;
        });
    }
    _destroy() {
        this._entries = undefined;
        if (this._pendingRefs) {
            this._pendingRefs.clear();
            this._pendingRefs = undefined;
        }
        if (this._cacheMap) {
            this._cacheMap.clear();
        }
        if (this._offsetReference) {
            this._offsetReference.clear();
        }
        if (this._objectStreamCollection) {
            this._objectStreamCollection.clear();
        }
        this._offsets = [];
        this._startXRefQueue = [];
        this._root = undefined;
        this._startXRefQueue = undefined;
        this._stream = undefined;
        this._streamState = undefined;
        this._tableState = undefined;
        this._topDictionary = undefined;
        this._trailer = undefined;
        this._version = undefined;
        this._crossReferencePosition = undefined;
    }
    _writeObjectCollection(objectCollection, buffer) {
        const objectStreamCollection = new Map();
        this._indexes = [];
        this._indexes.push(0, 1);
        objectCollection.forEach((value, key) => {
            this._writeObjectToBuffer(key, value, buffer, objectStreamCollection);
        });
        if (this._cacheMap.size > objectCollection.size) {
            this._cacheMap.forEach((value, key) => {
                if (!objectCollection.has(key)) {
                    this._writeObjectToBuffer(key, value, buffer, objectStreamCollection);
                }
            });
        }
        if (this._document.fileStructure._crossReferenceType === PdfCrossReferenceType.stream) {
            this._objectStream = undefined;
            this._objectStreamCollection = objectStreamCollection;
            this._writeXrefStream(buffer);
        }
        else {
            this._writeXrefTable(buffer);
        }
    }
    _writeArchiveStream(objectStreamCollection, key, value) {
        if (typeof this._objectStream === 'undefined' || this._objectStream._length === 100) {
            const archiveObj = new _PdfArchievedStream(this);
            objectStreamCollection.set(archiveObj._reference, archiveObj);
            this._objectStream = archiveObj;
        }
        this._objectStream._writeObject(key, value);
    }
    _writeObjectToBuffer(key, value, buffer, // eslint-disable-line
    objectStreamCollection) {
        let cipher;
        if (value instanceof _PdfDictionary && value.isCatalog) {
            this._writeToBuffer(buffer, key, value);
        }
        else if (value instanceof _PdfDictionary) {
            const type = value.get('Filter');
            const typeIsFilter = type && type.name === 'Standard';
            if (this._document.fileStructure._crossReferenceType === PdfCrossReferenceType.stream) {
                if (!typeIsFilter) {
                    this._writeArchiveStream(objectStreamCollection, key, value);
                }
                else {
                    this._writeToBuffer(buffer, key, value);
                }
            }
            else {
                this._offsetReference.set(key, buffer.length);
                this._indexes.push(key.objectNumber, 1);
                this._writeObject(value, buffer, key);
            }
        }
        else {
            if (value instanceof _PdfBaseStream) {
                const dictionary = value.dictionary;
                if (dictionary && dictionary._updated && !dictionary.isCatalog) {
                    if (this._encrypt) {
                        cipher = this._encrypt._createCipherTransform(key.objectNumber, key.generationNumber);
                    }
                    dictionary._updated = false;
                }
            }
            else if ((!Array.isArray(value) || value.length === 0) && typeof value !== 'number') {
                return;
            }
            this._writeToBuffer(buffer, key, value, cipher);
        }
    }
    _writeToBuffer(buffer, key, value, cipher) {
        this._offsets.push(buffer.length);
        this._offsetReference.set(key, buffer.length);
        this._indexes.push(key.objectNumber, 1);
        this._writeObject(value, buffer, key, cipher);
    }
    _getSortedReferences(collection) {
        let entriesArray = []; // eslint-disable-line
        collection.forEach((value, key) => {
            entriesArray.push([key, value]);
        });
        entriesArray.sort((a, b) => {
            return a[0].objectNumber - b[0].objectNumber;
        });
        let sortedCollection = new Map(); // eslint-disable-line
        let lastObjectNumber = 1;
        for (const [key, value] of entriesArray) {
            const currentObjectNumber = key.objectNumber;
            while (lastObjectNumber < currentObjectNumber) {
                sortedCollection.set({ objectNumber: lastObjectNumber }, 0);
                lastObjectNumber++;
            }
            sortedCollection.set(key, value);
            lastObjectNumber = currentObjectNumber + 1;
        }
        return sortedCollection;
    }
}
class _PdfObjectInformation {
}
class _PdfCrossTableState {
}
class _PdfStreamState {
}
class _PdfArchievedStream {
    constructor(crossReference) {
        this._indexes = '';
        this._length = 0;
        this._crossReference = crossReference;
        this._reference = crossReference._getNextReference();
        this._archiveXRef = '';
        this._updatedStream = [];
        this._collection = [];
    }
    _writeObject(key, value) {
        this._archiveXRef += `${key.objectNumber} ${this._updatedStream.length}${this._crossReference._newLine}`;
        this._collection.push(key.objectNumber, 1);
        this._crossReference._writeObject(value, this._updatedStream);
        this._length++;
    }
    _save(buffer, currentLength) {
        const data = [];
        this._crossReference._writeString(this._archiveXRef, data);
        this._crossReference._writeBytes(this._updatedStream, data);
        const newDict = new _PdfDictionary(this._crossReference);
        newDict.set('Type', _PdfName.get('ObjStm'));
        newDict.set('N', this._length);
        newDict.set('First', this._archiveXRef.length);
        newDict.set('Length', data.length);
        const archiveStream = new _PdfStream(data, newDict, 0, data.length);
        this._archiveOffset = currentLength + buffer.length;
        let cipher;
        if (this._crossReference._encrypt) {
            cipher = this._crossReference._encrypt._createCipherTransform(this._reference.objectNumber, this._reference.generationNumber);
        }
        this._crossReference._writeObject(archiveStream, buffer, this._reference, cipher);
    }
}
class _PdfMainObjectCollection {
    /**
     * Initializes a new instance of the `_PdfMainObjectCollection` class.
     *
     * @private
     * @param { _PdfCrossReference } collection - The cross-reference collection containing the PDF objects.
     * ```typescript
     * // Load an existing PDF document
     * let document: PdfDocument = new PdfDocument(data);
     * // Create a new object collection instance
     * let mainObjectCollection = new _PdfMainObjectCollection(document._crossReference);
     * // Access the main object collection
     * let objects = mainObjectCollection._mainObjectCollection;
     * // Save the document
     * document.save('output.pdf');
     * // Destroy the document
     * document.destroy();
     */
    constructor(collection) {
        this._pointer = 0;
        if (!(collection._cacheMap instanceof Map)) {
            throw new Error('Expected _cacheMap to be a Map.');
        }
        this._reference = [];
        this._cache = collection._cacheMap;
        this._mainObjectCollection = new Map(); // eslint-disable-line
        let foundCatalog = false;
        this._crossReference = collection;
        this._cache.forEach((value, key) => {
            if (!foundCatalog && value instanceof _PdfDictionary && value.isCatalog) {
                this._addToMainObjectCollection(key, value);
                foundCatalog = true;
            }
        });
        this._parseObjectCollection();
    }
    _parseObjectCollection() {
        while (this._pointer < this._mainObjectCollection.size) {
            const collection = new Map(); // eslint-disable-line
            let currentIndex = 0;
            this._mainObjectCollection.forEach((value, key) => {
                if (currentIndex === this._pointer) {
                    collection.set(key, value);
                    this._parse(key, value);
                }
                currentIndex++;
            });
            this._pointer++;
        }
        this._addReferencesToMainCollection();
        return this._mainObjectCollection;
    }
    _addToMainObjectCollection(key, value) {
        this._reference.push(key);
        this._mainObjectCollection.set(key, value);
    }
    _parseFetchValue(reference) {
        const fetchvalue = this._crossReference._fetch(reference); // eslint-disable-line
        this._parse(reference, fetchvalue);
    }
    _parse(key, value) {
        if (value instanceof _PdfDictionary) {
            this._parseDictionary(value);
        }
        else if (value instanceof _PdfBaseStream) {
            this._parseStream(key, value);
        }
        else if (value instanceof _PdfReference) {
            this._parseFetchValue(value);
        }
        else if (Array.isArray(value) && value.length > 0) {
            const isPdfReferenceArray = value.every((value) => value instanceof _PdfReference); // eslint-disable-line
            if (isPdfReferenceArray) {
                value.forEach((ref) => this._parseFetchValue(ref)); // eslint-disable-line
            }
            else {
                value.forEach((item) => {
                    if (item instanceof _PdfReference) {
                        this._parseFetchValue(item);
                    }
                });
                if (this._reference.indexOf(key) === -1 && !this._mainObjectCollection.has(key)) {
                    this._addToMainObjectCollection(key, value);
                }
            }
        }
        else if (typeof value === 'number') {
            if (this._reference.indexOf(key) === -1 && !this._mainObjectCollection.has(key)) {
                this._addToMainObjectCollection(key, value);
            }
        }
    }
    _addReferencesToMainCollection() {
        const objectsToWrite = []; // eslint-disable-line
        this._cache.forEach((value, key) => {
            if (!this._mainObjectCollection.has(key)) {
                objectsToWrite.push({ key, value });
            }
        });
        objectsToWrite.forEach(({ key, value }) => {
            this._addToMainObjectCollection(key, value);
        });
    }
    _parseDictionary(element) {
        element.forEach((key, value) => {
            const processReference = (ref) => {
                if (!this._mainObjectCollection.has(ref) && this._reference.indexOf(ref) === -1) {
                    let fetchValue = this._crossReference._fetch(ref); // eslint-disable-line
                    if (fetchValue instanceof _PdfReference) {
                        fetchValue = this._crossReference._fetch(fetchValue);
                    }
                    if (fetchValue instanceof _PdfBaseStream) {
                        this._parseStream(ref, fetchValue);
                    }
                    else {
                        this._addToMainObjectCollection(ref, fetchValue);
                    }
                }
            };
            if (value instanceof _PdfReference) {
                processReference(value);
            }
            else if (Array.isArray(value)) {
                value.forEach((item) => {
                    if (item instanceof _PdfReference) {
                        processReference(item);
                    }
                    else if (item instanceof _PdfDictionary) {
                        this._parseDictionary(item);
                    }
                });
            }
            else if (value instanceof _PdfDictionary) {
                this._parseDictionary(value);
            }
        });
    }
    _parseStream(key, element) {
        this._parseDictionary(element.dictionary);
        if (this._reference.indexOf(key) === -1 && !this._mainObjectCollection.has(key)) {
            const type = element.dictionary.get('Type');
            const subtype = element.dictionary.get('Subtype');
            const isUpdated = element.dictionary._updated;
            let uncompressedValue;
            if (isUpdated || (type && (type.name === 'XObject' || type.name === 'Metadata') &&
                (subtype.name === 'Form' || subtype.name === 'XML'))) {
                uncompressedValue = this._crossReference._fetch(key);
            }
            else {
                uncompressedValue = this._crossReference._fetch(key, true);
                uncompressedValue._isCompress = false;
            }
            this._addToMainObjectCollection(key, uncompressedValue);
        }
    }
}
